import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Search, AlertCircle, Hospital, MessageCircleWarning, ChevronRight, Syringe, ChevronDown, ChevronUp, BookOpen, Microscope, University, X, Send, Stethoscope, FlaskConical, PartyPopper, Heart, Wrench, Bell, UserRound } from 'lucide-react';
import { observanceFor } from '../../utils/observances';
import HospitalIcon from '../shared/icons/HospitalIcon';
import LaryngoscopeIcon from '../shared/icons/LaryngoscopeIcon';
import AppearanceControl from '../shared/AppearanceControl';
import ProfileSheet, { ProfileButton } from '../profile/ProfileSheet';
import { useProfile, homeHospitalId } from '../../services/profile';
import { useNotifications } from '../../services/notifications';
import { isInstalled } from '../../services/push';
import { readJSON, writeJSON } from '../../services/deviceStore';
import updateNotes from '../../changelog.json';
import { useBackHandler } from '../../hooks/NavigationContext';
import { db } from '../../firebase';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { notify } from '../../utils/notify';
import { SkeletonRow } from '../shared/Skeleton';

// One of the three primary destinations on the home screen. Same tile, type
// and chevron treatment on each; only the gradient changes.
const HeroCard = ({ title, subtitle, icon: Icon, gradient, onClick }) => (
  <button
    onClick={onClick}
    className={`w-full bg-gradient-to-br ${gradient} text-white rounded-2xl p-4 shadow-hero ring-1 ring-inset ring-white/10 hover:brightness-110 transition-all text-left flex items-center justify-between`}
  >
    <div className="flex items-center gap-3.5">
      <div className="w-11 h-11 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
        <Icon size={22} className="text-white" />
      </div>
      <div>
        <div className="font-semibold text-[17px] leading-tight">{title}</div>
        <div className="text-[13px] text-white/70 mt-0.5">{subtitle}</div>
      </div>
    </div>
    <ChevronRight size={20} className="text-white/50 flex-shrink-0" />
  </button>
);

// Where a report is about, so an email about a stale number names the page without a follow-up
const FEEDBACK_AREAS = ['Home', 'Emergency Protocols', 'Education Center', 'Clinical Tools', 'Kudos & Concerns', 'Needlestick', 'BCM Anesthesiology', 'BCM Resources', 'Research Hub'];
const feedbackInput = 'w-full px-3 py-2.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:border-emerald-600 focus:outline-none disabled:opacity-50';

// Days the header marks (Ether Day, CRNA Week, and so on); see utils/observances.js
const OBSERVANCE_ICONS = { flask: FlaskConical, party: PartyPopper, heart: Heart, wrench: Wrench };

const HomeScreen = ({ hospitals, onHospitalClick, onEmergencyClick, onEducationClick, onClinicalToolsClick, onConcernsClick, onNeedlestickClick, onBCMAnesthesiologyClick, onBCMResourcesClick, onResearchHubClick, onSearchClick, onUpdateClick, onTeeCertificationClick, updateReady = false, hideLaryngoscopeIcon = false }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [quickAccessExpanded, setQuickAccessExpanded] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  // Five taps on the laryngoscope: a short intubation, then the credits
  const taps = useRef([]);
  const [intubating, setIntubating] = useState(false);
  const [showCredits, setShowCredits] = useState(false);
  const [showProfile, setShowProfile] = useState(false);
  const [sheetStart, setSheetStart] = useState(null); // 'inbox' opens the sheet on Notifications
  const notifications = useNotifications();
  const openProfile = (where = null) => { setSheetStart(where); setShowProfile(true); };
  // One-time profile nudge, shown on the first open after install (or with ?nudge for reviewing). Either tap ends it for good.
  const NUDGE_KEY = 'profile-nudge-done';
  const [nudge, setNudge] = useState(() => !readJSON(NUDGE_KEY, false) && (isInstalled() || /[?&]nudge/.test(window.location.search)));
  const endNudge = () => { try { writeJSON(NUDGE_KEY, true); } catch { /* ignore */ } setNudge(false); };
  // A notification's "Open …" button
  const openPage = (id) => ({ education: onEducationClick, 'clinical-tools': onClinicalToolsClick, emergency: onEmergencyClick, 'bcm-anesthesiology': onBCMAnesthesiologyClick, 'bcm-resources': onBCMResourcesClick, 'research-hub': onResearchHubClick, concerns: onConcernsClick, needlestick: onNeedlestickClick, 'tee-certification': onTeeCertificationClick }[id] || (() => {}))();
  // Opened from a push notification: go straight to the inbox, then drop the parameter from the address
  useEffect(() => {
    if (!/[?&]notification=/.test(window.location.search)) return;
    setSheetStart('inbox'); setShowProfile(true);
    try { const url = new URL(window.location.href); url.searchParams.delete('notification'); window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`); } catch { /* ignore */ }
  }, []);
  const profile = useProfile();
  const showNudge = nudge && !(profile && profile.role);
  // The profile's hospital (a resident's current rotation, otherwise their site) goes first
  const homeId = homeHospitalId(profile);
  const orderedHospitals = useMemo(() => (homeId ? [...hospitals].sort((a, b) => (a.id === homeId ? -1 : b.id === homeId ? 1 : 0)) : hospitals), [hospitals, homeId]);
  const closeProfile = useCallback(() => setShowProfile(false), []);
  const closeCredits = useCallback(() => setShowCredits(false), []);
  useBackHandler(showCredits, closeCredits);
  const tapScope = () => {
    const now = Date.now();
    taps.current = [...taps.current.filter((t) => now - t < 2500), now];
    if (taps.current.length < 5 || intubating) return;
    taps.current = [];
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) { setShowCredits(true); return; }
    setIntubating(true);
    setTimeout(() => { setIntubating(false); setShowCredits(true); }, 1500);
  };
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackType, setFeedbackType] = useState('outdated');
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [feedbackPage, setFeedbackPage] = useState('');
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const closeChangelog = useCallback(() => setShowChangelog(false), []);
  const closeFeedback = useCallback(() => setShowFeedback(false), []);
  useBackHandler(showChangelog, closeChangelog);
  useBackHandler(showFeedback, closeFeedback);

  // Debounced search navigation
  useEffect(() => {
    if (searchQuery.length >= 2) {
      const timeoutId = setTimeout(() => {
        onSearchClick(searchQuery);
      }, 400); // 400ms debounce

      return () => clearTimeout(timeoutId);
    }
  }, [searchQuery, onSearchClick]);

  // Lock body scroll when modals are open
  useEffect(() => {
    if (showChangelog || showFeedback) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [showChangelog, showFeedback]);

  const handleSubmitFeedback = async () => {
    if (!feedbackNotes.trim()) {
      notify.warn('Please enter some feedback before submitting.');
      return;
    }

    setSubmittingFeedback(true);

    try {
      await addDoc(collection(db, 'feedback'), {
        type: feedbackType,
        page: feedbackPage || null,
        notes: feedbackNotes,
        timestamp: serverTimestamp(),
        appVersion: updateNotes.currentVersion,
        userAgent: navigator.userAgent
      });

      setFeedbackSubmitted(true);
      setFeedbackNotes('');

      // Auto-close after 2 seconds
      setTimeout(() => {
        setShowFeedback(false);
        setFeedbackSubmitted(false);
        setFeedbackType('bug');
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      notify.error('Failed to submit feedback. Please try again.');
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const otherQuickActions = [
    { name: 'Kudos & Concerns', icon: MessageCircleWarning, color: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300 border-amber-500 dark:border-amber-600', description: 'Praise, feedback & formal reports', onClick: onConcernsClick },
    { name: 'Needlestick Injury', icon: Syringe, color: 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-300 border-rose-600 dark:border-rose-600', description: 'Hospital-Specific Protocols', onClick: onNeedlestickClick },
    { name: 'Research Hub', icon: Microscope, color: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300 border-emerald-700 dark:border-emerald-600', description: 'Research Resources & Active Projects', onClick: onResearchHubClick },
    { name: 'BCM Anesthesiology', icon: Hospital, color: 'bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300 border-sky-600 dark:border-sky-600', description: 'Departmental Resources & Policies', onClick: onBCMAnesthesiologyClick },
    { name: 'Baylor College of Medicine', icon: University, color: 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-200 border-slate-500 dark:border-slate-600', description: 'College-Wide Resources & Policies', onClick: onBCMResourcesClick }
  ];

  return (
    <div className="max-w-md mx-auto bg-gray-50 dark:bg-gray-900 min-h-screen">
      <div className="bg-gradient-to-r from-slate-800 to-slate-700 dark:from-slate-900 dark:to-black text-white p-6 pb-8 relative">
        {/* Dark Mode Toggle - Top Right */}
        <div className="absolute top-5 right-5 z-10">
          <ProfileButton onClick={() => openProfile(notifications.unread > 0 ? 'inbox' : null)} profile={profile} alert={notifications.unread > 0} />
        </div>

        <div className="flex items-center justify-between mb-4 px-0">
          <div className="flex-shrink-0">
            <h1 className="font-merriweather text-3xl sm:text-4xl font-bold">BCM<br />Anesthesiology</h1>
            {notifications.unread > 0 ? (
              <button type="button" onClick={() => openProfile('inbox')} className="text-amber-200 text-sm mt-1 flex items-center gap-1.5 text-left max-w-[15rem]"><Bell size={14} className="text-amber-300 flex-shrink-0" /><span className="truncate">{notifications.unread === 1 ? `New: ${notifications.items.find((n) => !n.read).title}` : `${notifications.unread} new notifications`}</span></button>
            ) : (
              <p className="text-blue-200 text-sm mt-1 flex items-center gap-1.5">{(() => { const o = observanceFor(); if (!o) return 'Department Resources'; const Icon = OBSERVANCE_ICONS[o.icon] || FlaskConical; return <><Icon size={14} className="text-amber-300 flex-shrink-0" /> {o.label}</>; })()}</p>
            )}
          </div>
          {/* Icon yields width on very narrow phones so the row never overflows the viewport */}
          <div className="min-w-0 h-32 flex items-center justify-end relative" data-laryngoscope-target>
            <button type="button" onClick={tapScope} aria-label="BCM Anesthesiology" className={`relative cursor-default select-none ${intubating ? 'scope-intubate' : ''}`} style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}>
              <LaryngoscopeIcon
                className="h-16 w-auto max-w-full text-white"
                // Hidden while the splash's flying laryngoscope is in the air; it lands exactly here
                style={{ opacity: hideLaryngoscopeIcon ? 0 : 1 }}
              />
            </button>
            {intubating && <img src={`${process.env.PUBLIC_URL}/splash-assets/breathing-tube.svg`} alt="" aria-hidden="true" className="tube-pass absolute pointer-events-none z-10" style={{ height: 120, right: 26, top: -6 }} />}
          </div>
        </div>

        {/* Search Bar in Header */}
        <div className="mt-4">
          <div className="bg-white/10 rounded-2xl ring-1 ring-white/15 focus-within:bg-white/15 focus-within:ring-white/40 transition-all">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60" size={20} />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for anything"
                className="w-full pl-12 pr-4 py-3.5 rounded-2xl focus:outline-none text-white bg-transparent placeholder-white/50 text-[15px]"
              />
            </div>
          </div>
        </div>
      </div>

      {showNudge && (
        <div className="px-6 mt-6">
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-card p-4 flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-gray-900 dark:bg-white text-white dark:text-gray-900 flex items-center justify-center flex-shrink-0"><UserRound size={20} /></div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] text-gray-900 dark:text-white leading-tight">Keep your saved items and case log on every device</div>
              <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 leading-snug">A profile takes a minute: your BCM email and a code. Optional.</p>
              <div className="flex items-center gap-4 mt-3">
                <button type="button" onClick={() => { endNudge(); openProfile(); }} className="px-4 py-2 rounded-xl text-sm font-semibold bg-gray-900 text-white dark:bg-white dark:text-gray-900">Set up</button>
                <button type="button" onClick={endNudge} className="text-sm font-medium text-gray-500 hover:underline">Not now</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Primary sections */}
      <div className="px-6 mt-6 space-y-3">
        <HeroCard
          title="Emergency Protocols"
          subtitle="Crisis management & memory aids"
          icon={AlertCircle}
          gradient="from-red-800 to-red-950"
          onClick={() => { onEmergencyClick(); }}
        />
        <HeroCard
          title="Education Center"
          subtitle="Videos, articles & learning resources"
          icon={BookOpen}
          gradient="from-emerald-800 to-emerald-950"
          onClick={() => { onEducationClick(); }}
        />
        <HeroCard
          title="Clinical Tools"
          subtitle="Point-of-care decision support"
          icon={Stethoscope}
          gradient="from-violet-800 to-violet-950"
          onClick={() => { onClinicalToolsClick(); }}
        />
      </div>

      {/* Collapsible Quick Access */}
      <div className="px-6 mt-4">
        <button
          onClick={() => setQuickAccessExpanded(!quickAccessExpanded)}
          className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 shadow-card hover:border-purple-500 dark:hover:border-purple-400 hover:shadow-card-hover transition-all flex items-center justify-between"
        >
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">More Resources</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{otherQuickActions.length} additional {otherQuickActions.length === 1 ? 'link' : 'links'}</p>
          </div>
          {quickAccessExpanded ? <ChevronUp size={20} className="text-gray-400 dark:text-gray-500" /> : <ChevronDown size={20} className="text-gray-400 dark:text-gray-500" />}
        </button>

        {quickAccessExpanded && (
          <div className="mt-3 space-y-3">
            {otherQuickActions.map((action, idx) => {
              const IconComponent = action.icon;
              // Define neon hover colors for each resource
              const hoverColors = [
                'hover:border-amber-500 dark:hover:border-amber-400',     // Report Concerns
                'hover:border-rose-600 dark:hover:border-rose-500',       // Needlestick
                'hover:border-emerald-700 dark:hover:border-emerald-500', // Research Hub
                'hover:border-sky-600 dark:hover:border-sky-500',         // BCM Anesthesiology
                'hover:border-slate-400 dark:hover:border-slate-300'      // Baylor College
              ];
              return (
                <button key={idx} onClick={() => { action.onClick(); }} className={`w-full ${action.color} border-2 rounded-xl p-4 shadow-card ${hoverColors[idx]} hover:shadow-card-hover transition-all text-left flex items-center justify-between`}>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-white dark:bg-gray-700 rounded-lg"><IconComponent size={24} /></div>
                    <div>
                      <div className="font-semibold">{action.name}</div>
                      <div className="text-xs opacity-75 mt-0.5">{action.description}</div>
                    </div>
                  </div>
                  <ChevronRight size={20} className="opacity-60" />
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="px-6 mt-8 pb-6">
        <h2 className="eyebrow mb-3">Hospital Rotations</h2>
        <div className="space-y-3">
          {hospitals.length === 0 && Array.from({ length: 6 }).map((_, i) => <SkeletonRow key={i} />)}
          {orderedHospitals.map((hospital, idx) => (
            <button key={idx} onClick={() => { onHospitalClick(hospital); }} className="w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl p-4 shadow-card hover:border-blue-500 dark:hover:border-cyan-400 hover:shadow-card-hover transition-all text-left flex items-center justify-between group">
              <div className="flex items-center gap-3.5">
               <div className="w-11 h-11 bg-white rounded-xl border border-gray-200 dark:border-gray-600 flex items-center justify-center flex-shrink-0">
                  <HospitalIcon type={hospital.id} className="w-7 h-7" />
                </div>
                <div className="font-semibold text-[15px] text-gray-900 dark:text-white flex-1">{hospital.name}</div>
              </div>
              <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors" />
            </button>
          ))}
        </div>
      </div>

      {/* Version Footer */}
      <div className="px-6 py-4 text-center space-y-3">
        <div className="flex justify-center"><AppearanceControl compact /></div>
        <button
          onClick={() => setShowChangelog(true)}
          className="text-xs text-gray-400 dark:text-gray-600 hover:text-blue-600 dark:hover:text-blue-400 transition-colors block w-full"
        >
          Version {updateNotes.currentVersion} • View Changelog
        </button>
        {updateReady && (
          <button
            onClick={onUpdateClick}
            className="mt-1 inline-flex items-center gap-1.5 text-xs font-medium text-blue-700 dark:text-blue-300"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-blue-600" aria-hidden="true" />
            An update is ready. Tap to install
          </button>
        )}
        <button
          onClick={() => setShowFeedback(true)}
          className="text-xs text-gray-400 dark:text-gray-600 hover:text-blue-600 dark:hover:text-blue-400 transition-colors block w-full"
        >
          Report an issue or send feedback
        </button>
        <p className="text-xs text-gray-400 dark:text-gray-600">
          © 2026 BCM Anesthesiology
        </p>
      </div>

      <div className="h-8"></div>

      {/* Changelog Modal */}
      <ProfileSheet open={showProfile} onClose={closeProfile} profile={profile} startIn={sheetStart} notifications={notifications} onOpenPage={openPage} />

      {showCredits && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn" onClick={() => setShowCredits(false)}>
          <div role="dialog" aria-label="Credits" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h2 className="font-display text-lg font-semibold text-gray-900 dark:text-white">BCM Anesthesiology App</h2>
              <button onClick={() => setShowCredits(false)} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700"><X size={20} /></button>
            </div>
            <div className="px-5 pt-1 pb-4 space-y-3 text-sm text-gray-700 dark:text-gray-300">
              <p className="text-xs text-gray-500 dark:text-gray-400">Version {updateNotes.currentVersion}</p>
              <p>Made in the Department of Anesthesiology, Baylor College of Medicine.</p>
              <p>Thomas Powell, MD · design and build</p>
              <p>Clinical content is written and reviewed by the faculty named on each item.</p>
              <button type="button" onClick={() => { setShowCredits(false); setShowChangelog(true); }} className="text-sm font-medium text-blue-700 dark:text-blue-300 hover:underline">Version history</button>
            </div>
          </div>
        </div>
      )}

      {showChangelog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50" onClick={() => setShowChangelog(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-md w-full max-h-[80vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-700 dark:to-blue-800 text-white p-4 rounded-t-2xl flex items-center justify-between z-10">
              <h2 className="text-xl font-bold">What's New</h2>
              <button
                onClick={() => setShowChangelog(false)}
                className="p-2 hover:bg-blue-700 dark:hover:bg-blue-900 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-6">
              {updateNotes.versions.map((versionInfo, versionIdx) => (
                <div key={versionIdx} className={versionIdx > 0 ? 'pt-6 border-t border-gray-200 dark:border-gray-700' : ''}>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white">Version {versionInfo.version}</h3>
                    <span className="text-sm text-gray-500 dark:text-gray-400">{versionInfo.date}</span>
                  </div>
                  <ul className="space-y-2">
                    {versionInfo.notes.map((note, idx) => (
                      <li key={idx} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                        <span className="text-blue-600 dark:text-blue-400 mt-0.5">•</span>
                        <span>{note}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Feedback Modal */}
      {showFeedback && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 animate-fadeIn" onClick={() => !submittingFeedback && setShowFeedback(false)}>
          <div role="dialog" aria-label="Report an issue or send feedback" onClick={(e) => e.stopPropagation()} className="w-full max-w-md bg-white dark:bg-gray-800 rounded-t-3xl sm:rounded-3xl shadow-2xl pb-[max(1rem,env(safe-area-inset-bottom))] animate-slide-up max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 pt-4 pb-1">
              <h2 className="font-display text-lg font-semibold text-gray-900 dark:text-white">Report an issue or send feedback</h2>
              <button onClick={() => setShowFeedback(false)} disabled={submittingFeedback} aria-label="Close" className="p-2 -m-2 rounded-full text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"><X size={20} /></button>
            </div>
            {feedbackSubmitted ? (
              <div className="px-5 py-8 text-center">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 flex items-center justify-center mx-auto mb-4"><Send size={26} /></div>
                <h3 className="font-semibold text-gray-900 dark:text-white mb-1">Sent</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">Thank you. It goes straight to the app team.</p>
              </div>
            ) : (
              <div className="px-5 pt-2 pb-4 space-y-4">
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-snug">Anonymous. Wrong numbers, stale content, broken links, ideas: all welcome.</p>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="eyebrow mb-1.5 block">What</label>
                    <select value={feedbackType} onChange={(e) => setFeedbackType(e.target.value)} disabled={submittingFeedback} className={feedbackInput}>
                      <option value="outdated">Wrong or outdated information</option>
                      <option value="bug">Something is broken</option>
                      <option value="feature">Suggest an improvement</option>
                      <option value="content">Request content</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="eyebrow mb-1.5 block">Where</label>
                    <select value={feedbackPage} onChange={(e) => setFeedbackPage(e.target.value)} disabled={submittingFeedback} className={feedbackInput}>
                      <option value="">Anywhere</option>
                      {FEEDBACK_AREAS.map((a) => <option key={a} value={a}>{a}</option>)}
                      {hospitals.map((h) => <option key={h.id} value={h.name}>{h.name}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="eyebrow mb-1.5 block">Details</label>
                  <textarea
                    value={feedbackNotes}
                    onChange={(e) => setFeedbackNotes(e.target.value)}
                    disabled={submittingFeedback}
                    placeholder="What did you see, and what should it be?"
                    rows={5}
                    className={`${feedbackInput} resize-none`}
                  />
                </div>
                <button
                  onClick={handleSubmitFeedback}
                  disabled={submittingFeedback || !feedbackNotes.trim()}
                  className="w-full bg-emerald-700 hover:bg-emerald-800 text-white font-semibold py-3 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submittingFeedback ? <><div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" /><span>Sending…</span></> : <><Send size={18} /><span>Send</span></>}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default HomeScreen;
